import { useState, useEffect, useCallback, useRef } from 'react';
import { Upload, Trash2, X, ChevronLeft, ChevronRight, MapPin } from 'lucide-react';
import { palette, transition } from '../../styles/tokens';
import { supabase } from '../../lib/supabase';
import { useAuthStore } from '../../stores/authStore';
import { ConfirmDialog } from './ConfirmDialog';
import { log } from '../../lib/logger';

export interface GridPhoto {
  id: string;
  storage_path: string;
  url: string;
  phase: string;
  caption: string | null;
  latitude: number | null;
  longitude: number | null;
  taken_at: string;
}

interface Props {
  entityType: 'work_order' | 'booking';
  entityId: string;
  tableName: 'work_order_photos' | 'booking_photos';
  foreignKey: 'work_order_id' | 'booking_id';
  phases: string[];
}

const PHASE_LABELS: Record<string, string> = { before: 'Before', during: 'During', after: 'After' };

export function PhotoGrid({ entityType, entityId, tableName, foreignKey, phases }: Props) {
  const business = useAuthStore(s => s.business);
  const user = useAuthStore(s => s.user);
  const [photos, setPhotos] = useState<GridPhoto[]>([]);
  const [lightbox, setLightbox] = useState(-1);
  const [uploading, setUploading] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const mountedRef = useRef(true);

  const fetchPhotos = useCallback(async () => {
    const { data } = await supabase.from(tableName).select('*')
      .eq(foreignKey, entityId).eq('is_deleted', false)
      .order('phase').order('created_at');
    if (!mountedRef.current) return;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- supabase row payload is typed `any` upstream; structural copy below is safe
    const mapped = (data ?? []).map((p: any) => ({
      ...p,
      url: supabase.storage.from('work-photos').getPublicUrl(p.storage_path).data.publicUrl,
    }));
    setPhotos(mapped);
  }, [entityId, tableName, foreignKey]);

  useEffect(() => { mountedRef.current = true; void fetchPhotos(); return () => { mountedRef.current = false; }; }, [fetchPhotos]);

  const handleUpload = useCallback(async (phase: string) => {
    const input = document.createElement('input');
    input.type = 'file'; input.accept = 'image/*'; input.multiple = true;
    input.onchange = async () => {
      if (!input.files?.length || !business?.id || !user?.id) return;
      setUploading(true);
      for (const file of Array.from(input.files)) {
        try {
          const photoId = crypto.randomUUID();
          const path = `${business.id}/${entityType === 'work_order' ? 'job' : 'booking'}/${entityId}/${photoId}.jpg`;
          const arrayBuffer = await file.arrayBuffer();
          await supabase.storage.from('work-photos').upload(path, arrayBuffer, { contentType: file.type || 'image/jpeg' });
          await supabase.from(tableName).insert({
            id: photoId, [foreignKey]: entityId, business_id: business.id,
            uploaded_by: user.id, storage_path: path, phase,
            file_size_bytes: file.size, mime_type: file.type || 'image/jpeg',
          });
          await supabase.from('activity_log').insert({
            business_id: business.id, entity_type: entityType, entity_id: entityId,
            activity_type: 'photo_uploaded', description: `Added ${phase} photo`, logged_by: user.id,
          });
        } catch (e) { log.error('Upload failed:', e); }
      }
      setUploading(false);
      void fetchPhotos();
    };
    input.click();
  }, [business, user, entityId, entityType, tableName, foreignKey, fetchPhotos]);

  const performDelete = useCallback(async () => {
    if (!confirmDelete) return;
    setDeleting(true);
    try {
      await supabase.from(tableName).update({ is_deleted: true }).eq('id', confirmDelete);
      if (business?.id && user?.id) {
        await supabase.from('activity_log').insert({
          business_id: business.id, entity_type: entityType, entity_id: entityId,
          activity_type: 'photo_deleted', description: 'Photo removed', logged_by: user.id,
        });
      }
      void fetchPhotos();
      if (lightbox >= photos.length - 1) setLightbox(l => Math.max(-1, l - 1));
      setConfirmDelete(null);
    } finally {
      setDeleting(false);
    }
  }, [confirmDelete, tableName, business, user, entityType, entityId, fetchPhotos, lightbox, photos.length]);

  const lbPhoto = lightbox >= 0 ? photos[lightbox] : null;

  return (
    <div style={{ borderTop: `1px solid ${palette.g150}`, paddingTop: 16, marginBottom: 16 }}>
      <div style={{ fontSize: 13, fontWeight: 600, color: palette.g700, marginBottom: 12 }}>
        Photos {photos.length > 0 && <span style={{ fontWeight: 400, color: palette.g400 }}>({photos.length})</span>}
      </div>

      {phases.map(phase => {
        const phasePhotos = photos.filter(p => p.phase === phase);
        return (
          <div key={phase} style={{ marginBottom: 14 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
              <span style={{ fontSize: 11, fontWeight: 600, color: palette.g500, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{PHASE_LABELS[phase] ?? phase}</span>
              <button onClick={() => handleUpload(phase)} disabled={uploading} style={{ display: 'flex', alignItems: 'center', gap: 3, padding: '3px 8px', borderRadius: 4, border: 'none', background: palette.accentWash, color: palette.accent, fontSize: 11, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}><Upload size={11} /> Upload</button>
            </div>
            {phasePhotos.length === 0 ? (
              <div style={{ padding: '12px 0', fontSize: 12, color: palette.g400, fontStyle: 'italic' }}>No {phase} photos</div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
                {phasePhotos.map(p => (
                  <div key={p.id} style={{ position: 'relative', borderRadius: 6, overflow: 'hidden', cursor: 'pointer', aspectRatio: '1' }} onClick={() => setLightbox(photos.indexOf(p))}>
                    <img src={p.url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0)', transition: `background ${transition.fast}` }} onMouseEnter={e => (e.currentTarget.style.background = 'rgba(0,0,0,0.3)')} onMouseLeave={e => (e.currentTarget.style.background = 'rgba(0,0,0,0)')}>
                      <button onClick={e => { e.stopPropagation(); setConfirmDelete(p.id); }} style={{ position: 'absolute', top: 4, right: 4, width: 24, height: 24, borderRadius: 12, border: 'none', background: 'rgba(0,0,0,0.5)', color: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Trash2 size={12} /></button>
                    </div>
                    {p.caption && <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, padding: '4px 6px', background: 'rgba(0,0,0,0.5)', fontSize: 10, color: '#fff' }}>{p.caption}</div>}
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}

      {/* Lightbox */}
      {lbPhoto && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 9999, background: 'rgba(0,0,0,0.92)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }} onClick={() => setLightbox(-1)}>
          <div style={{ position: 'absolute', top: 16, right: 16, display: 'flex', gap: 8 }}>
            <button onClick={e => { e.stopPropagation(); setConfirmDelete(lbPhoto.id); }} style={{ width: 36, height: 36, borderRadius: 18, border: 'none', background: 'rgba(255,255,255,0.15)', color: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Trash2 size={16} /></button>
            <button onClick={() => setLightbox(-1)} style={{ width: 36, height: 36, borderRadius: 18, border: 'none', background: 'rgba(255,255,255,0.15)', color: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><X size={18} /></button>
          </div>
          {lightbox > 0 && <button onClick={e => { e.stopPropagation(); setLightbox(l => l - 1); }} style={{ position: 'absolute', left: 16, top: '50%', transform: 'translateY(-50%)', width: 40, height: 40, borderRadius: 20, border: 'none', background: 'rgba(255,255,255,0.1)', color: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><ChevronLeft size={22} /></button>}
          {lightbox < photos.length - 1 && <button onClick={e => { e.stopPropagation(); setLightbox(l => l + 1); }} style={{ position: 'absolute', right: 16, top: '50%', transform: 'translateY(-50%)', width: 40, height: 40, borderRadius: 20, border: 'none', background: 'rgba(255,255,255,0.1)', color: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><ChevronRight size={22} /></button>}
          <img src={lbPhoto.url} alt="" style={{ maxWidth: '85vw', maxHeight: '75vh', borderRadius: 8, objectFit: 'contain' }} onClick={e => e.stopPropagation()} />
          <div style={{ marginTop: 12, textAlign: 'center', color: 'rgba(255,255,255,0.7)' }}>
            <span style={{ padding: '2px 10px', borderRadius: 4, background: 'rgba(255,255,255,0.15)', fontSize: 12, fontWeight: 600, color: '#fff' }}>{PHASE_LABELS[lbPhoto.phase] ?? lbPhoto.phase}</span>
            {lbPhoto.caption && <div style={{ fontSize: 13, marginTop: 6, color: '#fff' }}>{lbPhoto.caption}</div>}
            <div style={{ fontSize: 11, marginTop: 4, display: 'flex', gap: 12, justifyContent: 'center' }}>
              <span>{new Date(lbPhoto.taken_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}</span>
              {lbPhoto.latitude != null && <span style={{ display: 'flex', alignItems: 'center', gap: 3 }}><MapPin size={10} />{lbPhoto.latitude.toFixed(4)}, {lbPhoto.longitude?.toFixed(4)}</span>}
              <span>{lightbox + 1} / {photos.length}</span>
            </div>
          </div>
        </div>
      )}

      <ConfirmDialog
        isOpen={!!confirmDelete}
        title="Delete this photo?"
        message="The photo will be removed from this record. This cannot be undone."
        confirmLabel="Delete photo"
        confirmVariant="danger"
        loading={deleting}
        onClose={() => { if (!deleting) setConfirmDelete(null); }}
        onConfirm={performDelete}
      />
    </div>
  );
}
